<script setup lang="ts">
/*
  **MedicalInsuranceRelief.employment** - This doesn't directly map to a specific field in the PDF form, as the PDF doesn't have an "employment" field in this section.

  **For each insurance in the insurances array:**

  **MedicalInsuranceRelief.insurances[n].nameOfPersonCoveredByPolicy** - Field (i) Name of person covered by policy

  **MedicalInsuranceRelief.insurances[n].amountOfGrossPremium** - Field (ii) Amount of the Gross premium attributable to this individual

  **MedicalInsuranceRelief.insurances[n].isChild** - Field (iii) If this individual is a 'child' insert ☒

  **MedicalInsuranceRelief.insurances[n].amountOfMonthsPolicyWasActive** - Field (v) No. of months in 2024 where the policy was active

  **Unmapped fields:**
  - **Field (iv) Amount of any contribution towards this premium made by you to your employer** - No corresponding form field exists
  - **MedicalInsuranceRelief.insurances[n].isForOfficeUseOnly** - No corresponding PDF field (this appears to be internal tracking)

  **Note:** The PDF has separate sections for "Self" and "Spouse or Civil Partner" but the current form structure doesn't distinguish between these categories. You may need to add a field to categorize each insurance entry as either for self or spouse/partner to properly map to the PDF structure.
*/
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import MainLayout from '@/components/layout/MainLayout.vue'
import { useRouter } from 'vue-router'
import { useForm11 } from '@/composables/useForm11'
import AvailableToClaim from '@/components/form11/AvailableToClaim.vue'
import AddMedicalInsuranceForm from '@/components/form11/AddMedicalInsuranceForm.vue'
import { faker } from '@faker-js/faker/locale/en'
import { useFormValidation } from '@/composables/useFormValidation'
import { validateRequiredSelection } from '@/lib/validation'
import { useQuestionState } from '@/composables/useQuestionState'

interface Insurance {
  nameOfPersonCoveredByPolicy: string
  amountOfGrossPremium: string
  amountOfMonthsPolicyWasActive: string
  isChild: boolean
  isForOfficeUseOnly: boolean
}

const formState = useQuestionState('MedicalInsuranceRelief', {
  employment: '',
  annualPremium: '',
})

const validationSchema = {
  employment: validateRequiredSelection,
}

const {
  errors,
  isValid,
  handleFieldTouch,
  markAllFieldsTouched,
  getInputClass,
} = useFormValidation(validationSchema, formState)

const router = useRouter()
const form11 = useForm11()
const insurances = ref<Insurance[]>([])
const isAddingInsurance = ref(false)

const payload = computed(() => ({
  selectedEmployment: formState.employment,
  insurances: insurances.value,
}))

const handleSubmit = () => {
  markAllFieldsTouched()
  if (!isValid.value) return

  if (insurances.value.length === 0) {
    alert('Please add at least one insurance')
    return
  }

  form11.goNext('MedicalInsuranceRelief', payload.value)
}

const handleAddInsurance = (insurance: Insurance) => {
  insurances.value.push(insurance)
  isAddingInsurance.value = false
}

const fillMockData = () => {
  formState.employment = faker.company.name()
  insurances.value = Array.from(
    { length: faker.number.int({ min: 1, max: 5 }) },
    () => ({
      nameOfPersonCoveredByPolicy: faker.person.fullName(),
      amountOfGrossPremium: faker.number
        .int({ min: 100, max: 1000 })
        .toString(),
      amountOfMonthsPolicyWasActive: faker.number
        .int({ min: 1, max: 12 })
        .toString(),
      isChild: faker.datatype.boolean(),
      isForOfficeUseOnly: faker.datatype.boolean(),
    }),
  )
}
</script>

<template>
  <MainLayout>
    <div class="max-w-3xl mx-auto p-6">
      <h1 class="text-2xl font-bold mb-6">Medical Insurance Relief</h1>

      <h2 class="text-lg font-semibold mb-3">Benefit-in-kind (BIK)</h2>

      <p class="mb-6">
        Medical insurance relief is due if your employer has paid a premium to
        an authorised medical insurance provider e.g. VHI, Laya, Aviva etc. on
        your behalf and this has been taxed as a BIK.
      </p>

      <p class="mb-6">
        Click <a href="#" class="text-primary underline">here</a> for more
        information
      </p>

      <Alert class="mb-6 bg-orange-50 border-orange-200">
        <AlertDescription>
          You cannot claim for legally enforceable maintenance payments for the
          benefit of your children. Voluntary maintenance payments to a former
          partner, or in respect of children, do not qualify for relief.
        </AlertDescription>
      </Alert>

      <div class="space-y-6">
        <!-- Employment Input -->
        <div class="space-y-2">
          <Label>Enter employment *</Label>
          <Input
            v-model="formState.employment"
            placeholder="Name of employment"
            :class="getInputClass('employment')"
            @blur="handleFieldTouch('employment')"
          />
          <p v-if="errors.employment" class="text-sm text-red-500">
            {{ errors.employment }}
          </p>
        </div>

        <!-- Insurance List -->
        <div v-if="insurances.length > 0" class="space-y-4">
          <div
            v-for="(insurance, index) in insurances"
            :key="index"
            class="p-4 border rounded-lg"
          >
            <p class="font-medium">
              {{ insurance.nameOfPersonCoveredByPolicy }}
            </p>
            <p class="text-sm text-gray-600">
              Premium: €{{ insurance.amountOfGrossPremium }}
            </p>
          </div>
        </div>

        <!-- Available to Claim -->
        <AvailableToClaim
          category="MedicalInsuranceRelief"
          :payload="payload"
        />

        <!-- Action Buttons -->
        <div class="flex gap-4">
          <Button class="flex-1" variant="secondary" @click="router.back()"
            >Cancel</Button
          >
          <Button
            class="flex-1"
            variant="outline"
            @click="isAddingInsurance = true"
            >Add Insurance</Button
          >
          <Button
            class="flex-1"
            @click="handleSubmit"
            :disabled="insurances.length === 0"
            >Next</Button
          >
        </div>

        <!-- Mock Data Button (Development Only) -->
        <Button @click="fillMockData" variant="outline" class="w-full">
          Fill Mock Data
        </Button>
      </div>
    </div>

    <AddMedicalInsuranceForm
      :open="isAddingInsurance"
      @submit="handleAddInsurance"
      @open-change="isAddingInsurance = $event"
    />
  </MainLayout>
</template>
